import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "fm_cookie_consent";
const CONSENT_VERSION = 1;

const DEFAULT_PREFS = { tecnicas: true, analiticas: false, marketing: false, version: CONSENT_VERSION, ts: null };

function readPrefs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (p?.version !== CONSENT_VERSION) return null;
    return p;
  } catch { return null; }
}
function writePrefs(p) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...p, version: CONSENT_VERSION, ts: new Date().toISOString() }));
}

// Hook for the rest of the app to know consent state.
export function useCookieConsent() {
  const [prefs, setPrefs] = useState(() => readPrefs());
  useEffect(() => {
    const onChange = () => setPrefs(readPrefs());
    window.addEventListener("fm_cookie_consent_change", onChange);
    return () => window.removeEventListener("fm_cookie_consent_change", onChange);
  }, []);
  return prefs;
}

export default function CookieBanner() {
  const [open, setOpen] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);

  useEffect(() => {
    const stored = readPrefs();
    if (!stored) {
      setOpen(true);
    } else {
      setPrefs(stored);
    }
    // Expose API to open from the cookies page
    window.__openCookieBanner = () => {
      setPrefs(readPrefs() || DEFAULT_PREFS);
      setShowPrefs(true);
      setOpen(true);
    };
    return () => { delete window.__openCookieBanner; };
  }, []);

  const persist = (p) => {
    writePrefs(p);
    setPrefs(p);
    setOpen(false);
    setShowPrefs(false);
    window.dispatchEvent(new Event("fm_cookie_consent_change"));
  };

  const acceptAll = () => persist({ ...DEFAULT_PREFS, tecnicas: true, analiticas: true, marketing: true });
  const rejectAll = () => persist({ ...DEFAULT_PREFS, tecnicas: true, analiticas: false, marketing: false });
  const saveSelection = () => persist(prefs);

  if (!open) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[100] bg-[#2d1f1a] text-white border-t-4 border-[#E0651F] shadow-2xl" data-testid="cookie-banner">
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-3 md:py-5">
        {!showPrefs ? (
          <>
            {/* Móvil: barra compacta de una línea */}
            <div className="md:hidden flex items-center gap-2">
              <Cookie className="w-5 h-5 text-[#E0651F] flex-shrink-0" />
              <p className="text-[11px] text-stone-300 leading-snug flex-1 min-w-0">
                Usamos cookies. <Link to="/politica-cookies" className="text-[#E0651F] underline">Info</Link>
              </p>
              <button
                onClick={() => setShowPrefs(true)}
                data-testid="cookie-configure"
                aria-label="Configurar cookies"
                className="px-2.5 py-1.5 text-[10px] tracking-[0.1em] uppercase font-bold bg-white/10 text-white border border-white/20 rounded-sm"
              >
                Config.
              </button>
              <button
                onClick={rejectAll}
                data-testid="cookie-reject"
                className="px-2.5 py-1.5 text-[10px] tracking-[0.1em] uppercase font-bold bg-stone-800 text-white rounded-sm"
              >
                No
              </button>
              <button
                onClick={acceptAll}
                data-testid="cookie-accept-all"
                className="px-3 py-1.5 text-[10px] tracking-[0.1em] uppercase font-bold bg-[#E0651F] text-white rounded-sm"
              >
                Aceptar
              </button>
            </div>
            {/* Desktop / tablet: layout original */}
            <div className="hidden md:flex flex-col md:flex-row md:items-center gap-5">
              <div className="flex items-start gap-3 flex-1">
                <Cookie className="w-7 h-7 text-[#E0651F] flex-shrink-0 mt-1" />
                <div>
                  <div className="font-display font-black text-base text-white mb-1">Tu privacidad nos importa.</div>
                  <p className="text-xs text-stone-300 leading-relaxed">
                    Usamos cookies técnicas (necesarias) y, con tu permiso, analíticas (Google Analytics) y de marketing.
                    Puedes aceptar todas, rechazarlas o configurarlas. Más información en nuestra <Link to="/politica-cookies" className="text-[#E0651F] underline hover:text-white">Política de Cookies</Link>.
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 md:flex-shrink-0">
                <button onClick={rejectAll}
                  className="px-4 py-2.5 text-[11px] tracking-[0.15em] uppercase font-bold bg-stone-800 hover:bg-stone-700 text-white rounded-sm transition-colors">Rechazar</button>
                <button onClick={() => setShowPrefs(true)}
                  className="px-4 py-2.5 text-[11px] tracking-[0.15em] uppercase font-bold bg-white/10 hover:bg-white hover:text-[#2d1f1a] text-white border border-white/30 rounded-sm transition-colors">Configurar</button>
                <button onClick={acceptAll}
                  className="px-5 py-2.5 text-[11px] tracking-[0.15em] uppercase font-bold bg-[#E0651F] hover:bg-white hover:text-[#2d1f1a] text-white rounded-sm transition-colors">Aceptar todas</button>
              </div>
            </div>
          </>
        ) : (
          <div data-testid="cookie-prefs-panel">
            <div className="flex items-center gap-3 mb-4">
              <Cookie className="w-6 h-6 text-[#E0651F]" />
              <div className="font-display font-black text-base">Configurar preferencias</div>
              <button onClick={() => setShowPrefs(false)} className="ml-auto text-stone-400 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            <div className="grid sm:grid-cols-3 gap-3 mb-5">
              <CategoryRow id="tecnicas" title="Técnicas" required prefs={prefs} setPrefs={setPrefs}
                desc="Esenciales para que el sitio funcione (sesión, preferencias). No requieren consentimiento." />
              <CategoryRow id="analiticas" title="Analíticas" prefs={prefs} setPrefs={setPrefs}
                desc="Google Analytics. Nos ayudan a entender qué páginas visitas para mejorar el servicio." />
              <CategoryRow id="marketing" title="Marketing" prefs={prefs} setPrefs={setPrefs}
                desc="Cookies para mostrar publicidad relevante en otros sitios y medir campañas." />
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              <button onClick={rejectAll} className="px-4 py-2 text-[11px] tracking-[0.15em] uppercase font-bold bg-stone-800 hover:bg-stone-700 text-white rounded-sm" data-testid="cookie-prefs-reject">Rechazar todas</button>
              <button onClick={saveSelection} className="px-4 py-2 text-[11px] tracking-[0.15em] uppercase font-bold bg-[#E0651F] hover:bg-white hover:text-[#2d1f1a] text-white rounded-sm" data-testid="cookie-prefs-save">Guardar mis preferencias</button>
              <button onClick={acceptAll} className="px-4 py-2 text-[11px] tracking-[0.15em] uppercase font-bold bg-white text-[#2d1f1a] hover:bg-stone-200 rounded-sm" data-testid="cookie-prefs-accept">Aceptar todas</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CategoryRow({ id, title, desc, prefs, setPrefs, required }) {
  const checked = required ? true : !!prefs[id];
  return (
    <label className={`flex flex-col p-4 rounded-sm border ${checked ? "border-[#E0651F] bg-[#E0651F]/10" : "border-stone-700 bg-stone-900"} ${required ? "opacity-90" : "cursor-pointer hover:bg-stone-800"}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="font-bold text-sm">{title}</span>
        <input type="checkbox" checked={checked} disabled={required}
          onChange={(e) => !required && setPrefs({ ...prefs, [id]: e.target.checked })}
          className="accent-[#E0651F] w-4 h-4" data-testid={`cookie-cat-${id}`} />
      </div>
      {required && <div className="text-[10px] uppercase tracking-wide text-[#E0651F] font-bold mb-1">Siempre activas</div>}
      <div className="text-[11px] text-stone-300 leading-relaxed">{desc}</div>
    </label>
  );
}
